import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../components/context/AuthProvider";

/* ─── content data ─────────────────────────────────────────────────────────── */

// Short labels for the sticky table of contents — the full titles live on the
// section headings themselves.
const TOC = [
  "Five words",
  "Quickstart",
  "Entry or Folio?",
  "Naming",
  "Types",
  "Quest anatomy",
  "Worked example",
  "One-shots",
  "Campaigns",
  "Session prep",
  "Between sessions",
  "Importing",
  "From paper",
  "Limits",
  "Anti-patterns",
];

const FIVE_WORDS: { word: string; means: React.ReactNode }[] = [
  {
    word: "Compendium",
    means: "Your private library. Everything you write lives here first. Nobody else can see it.",
  },
  {
    word: "Entry",
    means: "One piece of your world: an NPC, a town, a quest, a monster, a cursed sword.",
  },
  {
    word: "Folio",
    means:
      "A named, ordered block inside an Entry — “Appearance”, “Secrets”, “Scene: The Docks”. Each one can be shown or hidden on its own. This is the most important word here.",
  },
  {
    word: "Weave Link",
    means: (
      <>
        An inline link from one Entry&rsquo;s text to another, written{" "}
        <code className="gcode">[[Warden Ilse Krow]]</code>. Your web of relationships.
      </>
    ),
  },
  {
    word: "Chronicle",
    means:
      "An actual game you are running. It receives copies of your Entries, which you can scribble on freely without touching the originals.",
  },
];

const EXTRA_WORDS = [
  {
    word: "Revelation",
    means: "granting one Folio to one character mid-session. This is how secrets come out.",
  },
  {
    word: "DM Notes",
    means:
      "a field on every Entry and Folio that can never be shown to anyone, by design. Your contingencies live here.",
  },
  {
    word: "Fate Formula",
    means:
      "a dice roll you save onto a creature or an NPC so you can fire it at the table without looking anything up. Yours only; players never see them.",
  },
];

const QUICKSTART = [
  ["Make a Folder Entry", "named after your adventure. Everything goes inside it."],
  ["Make a Quest Entry", "with the same name. This is the spine."],
  ["Add a “Hook” Folio", "to the Quest — two or three sentences the players can read on day one."],
  ["Add a “The Situation” Folio", "— what is actually going on. Keep it hidden."],
  ["Add an “Objectives” Folio", "— bulleted goals and what counts as success."],
  ["Write one Folio per scene", "in play order: “Scene: The Docks”, “Scene: The Warehouse”."],
  [
    "Make an Entry for every place and person",
    "your scenes mention, and link them from the scene Folios with [[ ]].",
  ],
  ["Add “Rewards” and “Outcomes” Folios", "at the end of the Quest."],
  ["Create a Chronicle", "and add your Entries to it, starting with the Folder."],
  ["Share the Hook Folio", "with your players. Reveal everything else through play."],
];

const TIERS = [
  ["Immediately, on meeting it", "A Folio you grant when you share the Entry"],
  ["At a dramatic beat, later", "A hidden Folio you grant mid-session"],
  ["Never — it is your machinery", "DM Notes, which cannot be shared at all"],
];

const NAMING = [
  ["The Old Mill (Saltmarsh)", "The Old Mill 2"],
  ["Brine Ghoul", "Beast — Brine Ghoul (CR 2)"],
  ["Ambush at Pier Nine", "Encounter 3"],
  ["The Salt Warden's Debt", "Quest 1 — Act 1 — Salt Warden"],
];

const TYPES = [
  [
    "World",
    "Your setting bible — one per setting",
    "Tone & Pillars, Cosmology, Timeline, House Truths",
  ],
  [
    "Geographical Element",
    "Regions, mountain ranges, seas",
    "Overview, Travel & Hazards, Who Lives Here",
  ],
  ["Location", "Anywhere the party can stand", "Read-Aloud, Features, Inhabitants, Secrets"],
  ["Character", "Named people", "Appearance, Voice & Manner, Motivation, History, Secrets"],
  ["Beast", "Creatures with stats", "Tactics, Habitat, Loot — plus saved dice rolls"],
  [
    "Organization",
    "Factions and groups with goals",
    "Goals, Structure, Notable Members, Resources, Secrets",
  ],
  ["Item", "Objects that matter", "Description, Powers, History, The Catch (hidden)"],
  [
    "Event",
    "Things that happened, or will",
    "What Everyone Knows, What Actually Happened (hidden)",
  ],
  ["Quest", "A playable objective arc", "See §6 — the Folios are the quest"],
  [
    "Encounter",
    "A prepared scene or fight",
    "Setup Read-Aloud, Combatants, Terrain & Tactics, Loot",
  ],
  ["Folder", "Grouping only, no content of its own", "None"],
];

type Visibility = "shared" | "hidden" | null;

const QUEST_SKELETON: { name: string; vis: Visibility; body: string }[] = [
  { name: "Hook", vis: "shared", body: "How the party gets pulled in." },
  { name: "The Situation", vis: "hidden", body: "What is actually going on — your summary." },
  { name: "Objectives", vis: null, body: "Bulleted goals and what counts as success." },
  {
    name: "Scene: The Docks",
    vis: null,
    body: "First beat. Links the place and the people in it.",
  },
  { name: "Scene: The Warehouse", vis: null, body: "Second beat. Links the Encounter Entry." },
  { name: "Rewards", vis: "hidden", body: "Reveal on completion." },
  { name: "Outcomes", vis: "hidden", body: "Success, failure, and “they walked away”." },
];

const WORKED_QUEST: { name: string; vis: Visibility; body: React.ReactNode }[] = [
  {
    name: "Hook",
    vis: "shared",
    body: "A woman in a salt-stained coat buys your table a round and does not sit down. “Warden Ilse Krow. I am told you settle debts.” She slides a wet ledger across the table; the top page lists eleven names, and three are crossed out in something darker than ink. “The Debt-Eaters will collect the rest by the new moon. That is four nights. I can pay you in coin or in silence — your choice.”",
  },
  {
    name: "The Situation",
    vis: "hidden",
    body: "Krow signed the ledger herself, eleven years ago, to keep the harbour from freezing over. The Debt-Eaters are not extortionists — they are a creditor, and the contract is real. The three crossed-out names were not murdered; they were collected, and they are still walking around the drowned warehouse. Krow wants the ledger destroyed, not the debt paid, and she has not mentioned that her own name is the twelfth entry.",
  },
  {
    name: "Objectives",
    vis: "shared",
    body: (
      <>
        <ul className="glist">
          <li>Find out who or what the Debt-Eaters are, before the new moon.</li>
          <li>Recover or destroy the Warden&rsquo;s Ledger.</li>
          <li>Decide what the eight remaining names are owed.</li>
        </ul>
        <p className="mt-2">
          <em>Success:</em> the ledger is out of the Debt-Eaters&rsquo; hands and the party knows
          what it cost. <em>Partial:</em> the ledger burns but a name goes with it.
        </p>
      </>
    ),
  },
  {
    name: "Scene: The Docks",
    vis: null,
    body: (
      <>
        Opens at <code className="gcode">[[Saltmarsh Docks]]</code> at low tide.{" "}
        <code className="gcode">[[Tobbin the Fence]]</code> is the only person who will name the
        Debt-Eaters out loud, and he wants the ledger for himself.
        <ul className="glist mt-2">
          <li>If they lean on Tobbin: he gives them the warehouse and lies about the tide.</li>
          <li>If they pay him: he gives them the warehouse and the tide, and follows them.</li>
          <li>
            If they ignore him: he sells them to <code className="gcode">[[The Debt-Eaters]]</code>{" "}
            within the hour — run <code className="gcode">[[Ambush at Pier Nine]]</code>.
          </li>
        </ul>
      </>
    ),
  },
  {
    name: "Scene: The Warehouse",
    vis: null,
    body: (
      <>
        Inside <code className="gcode">[[The Drowned Warehouse]]</code> the water is knee-deep and
        rising an inch every ten minutes — the party has roughly forty minutes before swimming.
        Three collected debtors wander the aisles as <code className="gcode">[[Brine Ghoul]]</code>s
        and still answer to their own names.{" "}
        <code className="gcode">[[The Warden&rsquo;s Ledger]]</code> is in the counting-office on
        the mezzanine; taking it triggers <code className="gcode">[[Warehouse Showdown]]</code>.
      </>
    ),
  },
  {
    name: "Rewards",
    vis: "hidden",
    body: "Krow pays 300 silver from the harbour fund — money she does not have the authority to spend. If the party keeps the ledger instead, they hold eight lives and a creditor who now knows their faces. That is worth more, and costs more.",
  },
  {
    name: "Outcomes",
    vis: "hidden",
    body: (
      <ul className="glist">
        <li>
          <strong>Ledger destroyed.</strong> The eight are free, Krow is not — her name burned last.
          She dies at dawn, and the harbour freezes by winter.
        </li>
        <li>
          <strong>Ledger kept.</strong> The Debt-Eaters open an account in the party&rsquo;s name.
          Sequel hook.
        </li>
        <li>
          <strong>Debt paid in full.</strong> Somebody at the table has to volunteer. Let them.
        </li>
        <li>
          <strong>They walk away.</strong> New moon passes; eight more ghouls in the warehouse and
          Krow is gone. The docks remember.
        </li>
      </ul>
    ),
  },
];

const WORKED_NPC: { name: string; vis: Visibility; body: React.ReactNode }[] = [
  {
    name: "Appearance",
    vis: "shared",
    body: "Fifties, sun-cracked, a harbour warden's grey coat gone white at the hem from salt. Left hand always gloved. Smells of tar and cold iron.",
  },
  {
    name: "Voice & Manner",
    vis: "shared",
    body: "Short sentences. Answers the question you asked and not the one you meant. Never sits down indoors. When she lies she becomes more polite.",
  },
  {
    name: "Motivation",
    vis: "shared",
    body: "Wants the harbour to survive the winter. Says so plainly, and means it — which is exactly why the party will trust her too far.",
  },
  {
    name: "History",
    vis: "hidden",
    body: (
      <>
        Eleven years ago the ice came early and the fleet was going to be crushed in the roads. She
        found a creditor who dealt in years rather than coin, and she signed. The harbour has not
        frozen since. <em>Grant this Folio when they learn she signed the ledger herself.</em>
      </>
    ),
  },
  {
    name: "Secrets",
    vis: "hidden",
    body: (
      <>
        The glove hides three fingers gone grey to the second knuckle — the collection has already
        started on her. She is the twelfth name.{" "}
        <em>Grant this to whoever is in the room when the glove comes off, and to nobody else.</em>
      </>
    ),
  },
];

const WORKED_BEAST: { name: string; vis: Visibility; body: string }[] = [
  {
    name: "Tactics",
    vis: null,
    body: "Waits submerged and rises behind the rearmost character. Fights until it takes fire damage, then retreats into water and circles. Will not touch anyone holding the ledger, and will say that person's debts aloud.",
  },
  {
    name: "Habitat",
    vis: null,
    body: "Flooded cellars, tidal warehouses, anywhere brackish water sits still for years. Never more than a hundred paces from where it died.",
  },
  {
    name: "Harvest & Loot",
    vis: null,
    body: "Salt-glands, worth 15 silver to an alchemist. Whatever it was carrying when it was collected — usually a name, written somewhere on its body.",
  },
];

const FORMULAS = [
  ["Claw — to hit", "1d20+4", "Every round"],
  ["Claw — damage", "2d6+2", "On a hit"],
  ["Rise from the water", "1d20+7", "Its ambush, once"],
  ["Brine Reek — resist", "1d20 vs 13", "First round adjacent"],
  ["How many rise tonight", "1d3+1", "Set-up, before the scene"],
];

const ONESHOT_ROWS = [
  ["Quest", "The Salt Warden's Debt — the spine, laid out as above"],
  ["Location", "Saltmarsh Docks · The Drowned Warehouse"],
  ["Character", "Warden Ilse Krow · Tobbin the Fence"],
  ["Organization", "The Debt-Eaters"],
  ["Beast", "Brine Ghoul — with its saved dice rolls"],
  ["Encounter", "Ambush at Pier Nine · Warehouse Showdown"],
  ["Item", "The Warden's Ledger"],
];

const CAMPAIGN_ROWS = [
  ["World: Emberfall", "The setting bible — a sibling of the folders, never their parent."],
  ["The Cast", "One Character per recurring person"],
  ["Factions", "Organizations"],
  ["Gazetteer", "Regions — each holding its own Locations"],
  ["Bestiary", "Beasts"],
  ["Arcs", "One folder per act, holding that act's Quests and Encounters"],
  ["Lore & Relics", "Events and Items"],
];

const BARGAIN = [
  "Write in the Compendium, always.",
  "Add to the Chronicle only what next session might touch. Prep is an add-and-share pass, not an import of everything.",
  "Scribble freely on the Chronicle copies — rename the tavern, kill the NPC, update the Status Folio. Your originals stay clean for the next campaign.",
  "If canon changes — you rewrote the villain's motive — edit the Compendium Entry and then mirror the change in the Chronicle copy by hand. Copies do not update themselves yet.",
  "Content born at the table (a tavern you improvised) can be created straight into the Chronicle. If it earns a recurring role, recreate it in the Compendium later.",
];

const PREP: [string, string, string][] = [
  [
    "0–3 min",
    "Read last session's Outcomes Folio.",
    "Whatever you scribbled there at the end of the night is your agenda. If it is empty, that is the real lesson.",
  ],
  [
    "3–8 min",
    "Name the two or three places they are likely to go.",
    "Not everywhere they could go. Add those Locations to the game if they are not already in it, and whoever is standing in them.",
  ],
  [
    "8–13 min",
    "Add the Encounters you might run,",
    "and check each one links its creatures. Glance at the creatures' saved rolls — if a roll you know you will need is missing, add it now, not at the table.",
  ],
  [
    "13–17 min",
    "Decide tonight's reveals.",
    "Write, on paper if you like, three lines: which hidden Folio, who gets it, and what has to happen first. This is the whole art of it.",
  ],
  [
    "17–20 min",
    "Share the opening.",
    "Whatever the party should walk in already knowing — the Hook, the tavern, the NPC they are meeting — goes out before they sit down, so the first ten minutes are play and not admin.",
  ],
];

const BETWEEN = [
  [
    "Write the Outcomes.",
    "On the quest they were actually doing, in its Outcomes Folio: what they did, what they left undone, what they now believe that is wrong. That last one is gold.",
  ],
  [
    "Record what got out.",
    "Which secrets landed, and with whom. Reveals are per character and you will forget which of them heard the confession in the alley.",
  ],
  [
    "Promote the improvised.",
    "The tavern you invented on the spot exists only in this game. If the party liked it, or if you did, recreate it in your library so it survives the campaign.",
  ],
  [
    "Mirror any canon changes.",
    "If the session changed something permanently true about your world — the villain's real motive, a city burning down — edit the library version too. The game's copies do not update themselves.",
  ],
];

const IMPORT_TIPS = [
  [
    "Put it under its own Folder",
    "if it did not come with one, so it is never tangled with your material. You can always merge later; unmerging is misery.",
  ],
  [
    "Expect name collisions.",
    "Names must be unique in your library, so an incoming “The Old Mill” will need distinguishing from yours. Add the place, not a number — see §4.",
  ],
  [
    "Read the author's hidden Folios and DM Notes before session one.",
    "Their reveal timing is a suggestion, and it is built around a table that is not yours.",
  ],
  [
    "Re-file, do not re-link.",
    "Move things where you would have filed them; the links inside the text keep working wherever the Entries end up. That is the point of links.",
  ],
];

const PASSES = [
  [
    "Pass 1 — the index.",
    "Read the whole thing and write down only the proper nouns: people, places, factions, named items and events. Do not create anything yet. This list is your Entry list, and it is usually shorter than you feared.",
  ],
  [
    "Pass 2 — the shells.",
    "Create every Entry on that list with its Type, its Category, and nothing else. Empty Entries are fine and this pass is fast. Now your links have somewhere to point.",
  ],
  [
    "Pass 3 — the spine.",
    "Build the Quest Entries, one Folio per beat, in play order. Where a beat mentions a person or place, link it. Do not paste the module's prose wholesale — write the one paragraph you would actually say out loud.",
  ],
  [
    "Pass 4 — the flesh.",
    "Fill in the shells, and here is where the work pays: as you write each one, ask the reveal question. Published adventures mix public description and GM secrets in the same paragraph constantly. Split every one you find.",
  ],
  [
    "Pass 5 — the table stuff.",
    "Save the rolls you will need onto the creatures, and put the module's boxed read-aloud text into its own Folio so you can hand it straight to the players.",
  ],
];

const LIMITS = [
  ["Entry names", "are 2–100 characters and must be unique across your whole Compendium."],
  ["Type and Category are both required,", "up to 50 characters each."],
  [
    "A description or a Folio holds up to 5,000 characters.",
    "Treat the cap as an editor, not an obstacle.",
  ],
  [
    "Type must come from the list of eleven.",
    "Capitalisation does not matter; invented types are rejected.",
  ],
  [
    "Hierarchy depth is unlimited",
    "— but four levels is the practical ceiling. Deeper trees stop being browsable at the table.",
  ],
  [
    "Tags are stored but do not filter yet.",
    "Do not build your organisation on them — put the load-bearing labels in Category.",
  ],
  ["DM Notes can never be revealed.", "Not by accident, not on purpose."],
];

const WORSE = [
  [
    "The wall of prose.",
    "One Entry holding the entire adventure. It cannot be revealed piece by piece, nothing can link into the middle of it, and it will hit the character cap. Split it.",
  ],
  [
    "Entry inflation.",
    "An Entry for the innkeeper's cat. It fails both tests — it is a bullet in the inn's “Features” Folio.",
  ],
  [
    "The secret hiding in plain sight.",
    "Putting the twist in the “History” Folio you already shared. Reveal timing decides where Folio boundaries go. Re-split.",
  ],
  [
    "DM Notes as content.",
    "If players might eventually learn it, it belongs in a hidden Folio. DM Notes are for mechanics, contingencies and pacing only.",
  ],
  [
    "Deep nesting.",
    "Depth is technically unlimited, but past four levels a tree stops being browsable when you need it fast. Flatten, and link instead.",
  ],
  ["Organising by tag.", "Tags do not filter yet. Category does."],
  ["Inventing a Type.", "“Sidequest” will simply be rejected. Use Quest, plus a Category."],
];

/* ─── presentation helpers ─────────────────────────────────────────────────── */

const Kicker: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mb-6 text-xs uppercase tracking-[0.45em] text-amber-400/80">{children}</p>
);

const Section: React.FC<{ num: number; title: string; children: React.ReactNode }> = ({
  num,
  title,
  children,
}) => (
  <section id={`s${num}`} className="scroll-mt-36 border-b border-zinc-900 py-14 last:border-0">
    <h2 className="mb-8 flex items-baseline gap-4 text-3xl font-light tracking-wide text-white md:text-4xl">
      <span className="font-mono text-lg text-amber-400">{num}</span>
      {title}
    </h2>
    {children}
  </section>
);

const VisChip: React.FC<{ vis: Visibility }> = ({ vis }) =>
  vis === null ? null : vis === "shared" ? (
    <span className="rounded-full border border-emerald-400/40 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-emerald-400">
      shared
    </span>
  ) : (
    <span className="rounded-full bg-zinc-800 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-amber-400/90">
      hidden
    </span>
  );

const EntryCard: React.FC<{
  title: string;
  tag: string;
  folios: { name: string; vis: Visibility; body: React.ReactNode }[];
  numbered?: boolean;
  dmNotes?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ title, tag, folios, numbered, dmNotes, children }) => (
  <div className="overflow-hidden rounded border border-zinc-800">
    <div className="flex items-center justify-between gap-4 bg-zinc-900 px-5 py-3">
      <h4 className="text-base font-normal tracking-wide text-white">{title}</h4>
      <span className="font-mono text-[10px] uppercase tracking-widest text-amber-400/80">
        {tag}
      </span>
    </div>
    <div className="divide-y divide-zinc-900 bg-zinc-950/60">
      {folios.map((f, i) => (
        <div key={f.name} className="flex gap-4 px-5 py-4">
          {numbered && (
            <span className="w-4 flex-shrink-0 font-mono text-xs text-zinc-600">{i + 1}</span>
          )}
          <div className="min-w-0">
            <p className="mb-1 flex items-center gap-2 text-sm text-white">
              {f.name} <VisChip vis={f.vis} />
            </p>
            <div className="text-sm leading-relaxed text-zinc-400">{f.body}</div>
          </div>
        </div>
      ))}
      {children}
    </div>
    {dmNotes && (
      <div className="border-t border-zinc-800 bg-zinc-900/60 px-5 py-4">
        <p className="mb-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          dm notes · never shareable
        </p>
        <div className="text-sm leading-relaxed text-zinc-400">{dmNotes}</div>
      </div>
    )}
  </div>
);

const TestBox: React.FC<{ label: string; question: string; children: React.ReactNode }> = ({
  label,
  question,
  children,
}) => (
  <div className="mb-4 rounded border border-zinc-800 bg-zinc-900/40 p-6">
    <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-amber-400/80">
      {label}
    </p>
    <p className="mb-2 text-lg font-light italic text-white">{question}</p>
    <div className="text-sm leading-relaxed text-zinc-400">{children}</div>
  </div>
);

const Aside: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="my-6 border-l-2 border-amber-400/60 pl-5 text-sm italic leading-relaxed text-zinc-400">
    {children}
  </div>
);

const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mb-4 max-w-3xl text-sm leading-relaxed text-zinc-400 md:text-base">{children}</p>
);

const H3: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="mb-4 mt-10 text-xl font-light tracking-wide text-white">{children}</h3>
);

/* ─── page ─────────────────────────────────────────────────────────────────── */

const AuthoringGuide: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Nav */}
      <header className="fixed left-0 right-0 top-0 z-50 flex items-center justify-between border-b border-zinc-900 bg-zinc-950/80 px-8 py-4 backdrop-blur-sm">
        <div className="flex select-none items-center gap-3">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo192.png" alt="RoleWeave" className="h-8 w-8 object-contain" />
            <span className="text-xl font-light tracking-widest">
              Role<span className="font-normal text-amber-400">Weave</span>
            </span>
          </Link>
          <span className="mx-1 text-zinc-800">·</span>
          <span className="text-xs uppercase tracking-widest text-zinc-500">Guide</span>
        </div>
        <nav className="flex items-center gap-6">
          <Link
            to="/lexicon"
            className="text-sm tracking-wide text-zinc-400 transition-colors duration-200 hover:text-white"
          >
            Lexicon
          </Link>
          <Link to="/guide" className="text-sm tracking-wide text-amber-400">
            Guide
          </Link>
          {isAuthenticated ? (
            <Link
              to="/protected/home"
              className="border border-amber-400/50 px-5 py-2 text-sm tracking-wide text-amber-400 transition-all duration-200 hover:bg-amber-400 hover:text-zinc-950"
            >
              Hearth →
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                className="text-sm tracking-wide text-zinc-400 transition-colors duration-200 hover:text-white"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                className="border border-amber-400/50 px-5 py-2 text-sm tracking-wide text-amber-400 transition-all duration-200 hover:bg-amber-400 hover:text-zinc-950"
              >
                Get started
              </Link>
            </>
          )}
        </nav>
      </header>

      {/* Hero */}
      <section className="border-b border-zinc-900 px-8 pb-16 pt-40">
        <div className="mx-auto max-w-4xl">
          <Kicker>Creator handbook</Kicker>
          <h1 className="mb-8 text-6xl font-thin leading-none tracking-tight md:text-8xl">
            The Authoring <span className="text-amber-400">Guide</span>
          </h1>
          <p className="mb-8 max-w-2xl text-base font-light italic leading-relaxed text-zinc-400">
            How to turn a one-shot, a quest, or a whole campaign into content that plays well at the
            table.
          </p>
          <P>
            The platform deliberately imposes very little structure on your writing. That is a gift
            and a trap: you can put anything anywhere, which means the shape of your adventure comes
            from <em>your</em> habits, not the software&rsquo;s. This guide gives you those habits —
            a small set of conventions that make prep faster, sessions smoother, and your material
            reusable.
          </P>
          <P>Read §1 and §2 before you write anything. The rest you can consult as you go.</P>
        </div>
      </section>

      {/* Sticky TOC — wraps on desktop, scrolls (scrollbar hidden) on mobile */}
      <div className="sticky top-[57px] z-40 border-b border-zinc-900 bg-zinc-950/90 backdrop-blur-sm">
        <div className="gtoc mx-auto flex max-w-5xl gap-2 overflow-x-auto px-8 py-3 md:flex-wrap md:justify-center md:overflow-x-visible">
          {TOC.map((t, i) => (
            <a key={t} href={`#s${i + 1}`} className="gpill">
              <span className="mr-1.5 text-amber-400/80">{i + 1}</span>
              {t}
            </a>
          ))}
        </div>
      </div>

      <main className="mx-auto max-w-4xl px-8">
        <Section num={1} title="The five words you need">
          <P>
            Everything in this guide is built from five ideas. Learn them once and the rest follows.
          </P>
          <div className="overflow-x-auto">
            <table className="gtable">
              <thead>
                <tr>
                  <th>Word</th>
                  <th>What it means</th>
                </tr>
              </thead>
              <tbody>
                {FIVE_WORDS.map((r) => (
                  <tr key={r.word}>
                    <td className="whitespace-nowrap font-normal text-white">{r.word}</td>
                    <td>{r.means}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <P>Three more you will meet along the way:</P>
          <ul className="glist max-w-3xl">
            {EXTRA_WORDS.map((r) => (
              <li key={r.word}>
                <strong className="text-white">{r.word}</strong> — {r.means}
              </li>
            ))}
          </ul>
        </Section>

        <Section num={2} title="Quickstart: your first quest">
          <P>
            Ten steps. Do them in order and you will have a playable quest and the muscle memory for
            everything else.
          </P>
          <ol className="max-w-3xl space-y-3">
            {QUICKSTART.map(([head, rest], i) => (
              <li key={head} className="flex gap-4 text-sm leading-relaxed text-zinc-400">
                <span className="w-5 flex-shrink-0 text-right font-mono text-xs text-amber-400/80">
                  {i + 1}
                </span>
                <span>
                  <strong className="text-white">{head}</strong> {rest}
                </span>
              </li>
            ))}
          </ol>
          <Aside>
            If step 7 feels like a lot of small Entries, good — that is the shape that pays off. The
            next section tells you where to stop.
          </Aside>
        </Section>

        <Section num={3} title="Entry, Folio, or bullet?">
          <P>
            Every sentence of your adventure is one of three things. Two questions decide, asked in
            this order.
          </P>
          <TestBox
            label="Question one · the link test"
            question="Will anything else point at it? Will players meet it as a “thing”?"
          >
            Then it is an <strong className="text-white">Entry</strong>. People, places, factions,
            items with history, named events. If you would ever write{" "}
            <code className="gcode">[[ ]]</code> to it — even once — it earns its own Entry.
          </TestBox>
          <TestBox
            label="Question two · the reveal test"
            question="Will part of this be shown at a different moment than the rest?"
          >
            Then that part is its own <strong className="text-white">Folio</strong>. You reveal one
            Folio at a time, so anything with different timing must sit in a different Folio. A
            guard captain&rsquo;s &ldquo;Appearance&rdquo; is public the moment they meet her; her
            &ldquo;True Allegiance&rdquo; surfaces in act three. Two Folios.
          </TestBox>
          <TestBox label="Neither?" question="Then it is not a container at all.">
            It is a <strong className="text-white">bullet inside a Folio you already have</strong>.
            The guard&rsquo;s name, the price of ale, tonight&rsquo;s weather. Do not make Entries
            out of texture.
          </TestBox>
          <H3>Secrets have exactly three tiers</H3>
          <P>
            And each tier has one correct home. Getting this right is most of what good structure
            means here.
          </P>
          <div className="overflow-x-auto">
            <table className="gtable">
              <thead>
                <tr>
                  <th>When players learn it</th>
                  <th>Where you write it</th>
                </tr>
              </thead>
              <tbody>
                {TIERS.map(([when, where]) => (
                  <tr key={when}>
                    <td className="text-white">{when}</td>
                    <td>{where}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section num={4} title="Naming things">
          <P>
            Names are the one thing you cannot be sloppy about. Every Entry name must be unique
            across your entire library, and the name is what you will type when you go looking for
            something mid-session with four people watching you. Two habits cover it.
          </P>
          <P>
            <strong className="text-white">Name it the way you would say it at the table.</strong>{" "}
            Not <em>NPC — Krow, Ilse (Warden)</em>. Just{" "}
            <code className="gcode">Warden Ilse Krow</code>. You are searching by memory, and memory
            speaks plainly. Never put the Type in the name — the icon already says it.
          </P>
          <P>
            <strong className="text-white">Disambiguate with place, not with numbers.</strong> When
            two things collide — and &ldquo;The Old Mill&rdquo; always collides eventually — add
            where it is, not which one it is:
          </P>
          <div className="overflow-x-auto">
            <table className="gtable">
              <thead>
                <tr>
                  <th>Do this</th>
                  <th>Not this</th>
                </tr>
              </thead>
              <tbody>
                {NAMING.map(([doThis, notThis]) => (
                  <tr key={doThis}>
                    <td className="text-white">{doThis}</td>
                    <td className="text-zinc-500 line-through decoration-zinc-700">{notThis}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <P>
            Folio names follow the same rule but shorter — one or two words, and reused across
            Entries so your library reads consistently. If every Character has a Folio called
            &ldquo;Secrets&rdquo;, you will never hunt for it. Invent a new Folio name only when the
            content genuinely is not one of your usual ones.
          </P>
          <P>
            Two smaller notes: names run from 2 to 100 characters, and leading &ldquo;The&rdquo; is
            fine — searching ignores where in the name your letters land.
          </P>
        </Section>

        <Section num={5} title="Picking a Type">
          <P>
            Every Entry has a Type. Types do not change how anything behaves — they set the icon,
            the colour, and the filter bucket. There are exactly eleven, and they are the only
            values accepted: anything you invent will be refused when you try to save.
          </P>
          <div className="overflow-x-auto">
            <table className="gtable">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Use for</th>
                  <th>Folios that usually work</th>
                </tr>
              </thead>
              <tbody>
                {TYPES.map(([type, useFor, folios]) => (
                  <tr key={type}>
                    <td className="whitespace-nowrap font-normal text-white">{type}</td>
                    <td>{useFor}</td>
                    <td>{folios}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <H3>Category is your second axis</H3>
          <P>
            Category is free text, and it is the one label you can actually filter by. Use it for
            the dimension Type does not cover — the act (<code className="gcode">Act 1</code>), the
            region (<code className="gcode">Northmarch</code>), or the role in your story (
            <code className="gcode">Villain</code>, <code className="gcode">Patron</code>). Pick{" "}
            <em>one</em> scheme for a project and hold to it. Categories filter; prose does not.
          </P>
        </Section>

        <Section num={6} title="Anatomy of a quest">
          <P>
            There is no quest engine. A quest is simply one Entry whose Folios <em>are</em> its
            structure, ordered top to bottom in the order you will play them. Drag to reorder any
            time.
          </P>
          <EntryCard
            title="The Salt Warden's Debt"
            tag="quest · act 1"
            folios={QUEST_SKELETON}
            numbered
            dmNotes={<>Contingencies, pacing warnings, &ldquo;if they kill the patron&rdquo;.</>}
          />
          <ul className="glist mt-8 max-w-3xl">
            <li>
              <strong className="text-white">Scenes are Folios, not separate Entries.</strong> A
              scene has no life outside its quest. The places and people <em>in</em> the scene are
              Entries, reached by link.
            </li>
            <li>
              <strong className="text-white">Encounters are separate Entries.</strong> They get
              revealed on their own timing and link their own monsters. Keep them beside the quest
              and link them from the scene Folio.
            </li>
            <li>
              <strong className="text-white">Quest status is a habit, not a feature.</strong>{" "}
              Nothing tracks progress for you. Put it in the Category (
              <code className="gcode">Act 1 — done</code>) or keep a one-line &ldquo;Status&rdquo;
              Folio you edit as you play.
            </li>
            <li>
              <strong className="text-white">
                If a scene will not fit in one Folio, it is two scenes.
              </strong>{" "}
              The length cap is a useful editor.
            </li>
          </ul>
        </Section>

        <Section num={7} title="Worked example">
          <P>
            Everything above is theory. Here is the same adventure written out for real — three
            Entries in full, so you can see what actually goes in the boxes. Copy the shapes, not
            the words.
          </P>
          <H3>The Quest, written out</H3>
          <EntryCard
            title="The Salt Warden's Debt"
            tag="quest · act 1"
            folios={WORKED_QUEST}
            numbered
            dmNotes="Two hours if you skip Pier Nine. If the party kills Krow early, Tobbin inherits the quest and wants a partner. The ghouls will not attack anyone holding the ledger — this is the clue that the contract is real, so make sure one of them notices before the showdown."
          />
          <H3>An NPC, written out</H3>
          <P>
            Same idea, different rhythm: the first three Folios are things a player can observe in
            one conversation, so they go out with the Entry. The last two are the story, and they
            wait.
          </P>
          <EntryCard
            title="Warden Ilse Krow"
            tag="character · patron"
            folios={WORKED_NPC}
            dmNotes="She cannot be persuaded to burn the ledger while her name is on it — she will not survive it and she knows. If the party works this out, she stops being a patron and becomes the problem. Do not soften her."
          />
          <H3>A Beast, written out — with Fate Formulas</H3>
          <P>
            Creatures earn one extra thing: the rolls you will actually need, saved onto the Entry
            so you can fire them without leafing through a book. Save the rolls you make{" "}
            <em>every</em> time, and nothing else — a list of twenty formulas is as slow as no
            formulas.
          </P>
          <EntryCard
            title="Brine Ghoul"
            tag="beast · saltmarsh"
            folios={WORKED_BEAST}
            dmNotes="Three of these are named debtors, not monsters. If a player calls one by name it hesitates for a round. Nobody gets told this; they have to try it."
          >
            <div className="px-5 py-4">
              <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                fate formulas · yours only
              </p>
              <table className="gtable !my-0">
                <thead>
                  <tr>
                    <th>Name it as you&rsquo;d call it</th>
                    <th>Roll</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {FORMULAS.map(([name, roll, when]) => (
                    <tr key={name}>
                      <td className="text-white">{name}</td>
                      <td>
                        <code className="gcode">{roll}</code>
                      </td>
                      <td>{when}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </EntryCard>
          <Aside>
            Notice what is not here: no Folio for the warehouse&rsquo;s layout (that lives in the
            Location), no Folio for the fight (that is the Encounter), no Entry for the alchemist
            who buys salt-glands (that is a bullet, until someone asks his name).
          </Aside>
        </Section>

        <Section num={8} title="Building a one-shot">
          <P>
            A four-hour one-shot becomes roughly{" "}
            <strong className="text-white">15–25 Entries under a single Folder</strong>. That count
            is the sweet spot. Fewer means you are pasting blobs of prose; many more means you made
            Entries out of texture.
          </P>
          <div className="overflow-hidden rounded border border-zinc-800">
            <div className="flex items-center gap-3 bg-zinc-900 px-5 py-3">
              <span className="rounded-full border border-zinc-700 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-zinc-400">
                folder
              </span>
              <h4 className="text-base font-normal tracking-wide text-white">
                The Salt Warden&rsquo;s Debt
              </h4>
            </div>
            <div className="divide-y divide-zinc-900 bg-zinc-950/60">
              {ONESHOT_ROWS.map(([type, items]) => (
                <div key={type + items} className="flex gap-6 px-5 py-3 text-sm">
                  <span className="w-28 flex-shrink-0 font-mono text-[10px] uppercase tracking-widest text-amber-400/70 pt-0.5">
                    {type}
                  </span>
                  <span className="text-zinc-300">{items}</span>
                </div>
              ))}
            </div>
          </div>
          <P>
            <br />A workable budget: one Quest, three to five Locations, four to eight Characters,
            one to three Beasts, two to four Encounters, one to three Items or Events. Keep it all
            flat under the Folder — one-shots do not need deep trees, and flatness makes what comes
            next cheap.
          </P>
          <H3>Running it</H3>
          <P>
            Create the Chronicle, then add Entries to it one at a time — there is no bulk add, and
            children do not come along with their parent. Add the Folder first, then the rest; the
            tree re-assembles itself as pieces arrive. Share the Quest&rsquo;s Hook Folio to open
            the session, and reveal the rest as it happens.
          </P>
          <H3>Sharing it with other people</H3>
          <P>
            Export the Folder together with everything under it, and you get one file with all the
            internal links rewritten so they still work for whoever imports it. The export will warn
            you about any link that points <em>outside</em> the bundle — fix those before you hand
            it over. That bundle is your one-shot, as a product.
          </P>
        </Section>

        <Section num={9} title="Building a campaign">
          <P>Same tests, same quest anatomy, plus two structural rules a one-shot never needed.</P>
          <H3>Rule one — several top-level Folders, not one World to rule them all</H3>
          <P>
            Keep your tree about four levels deep. If you nest everything under a single World
            Entry, you have spent your first level on ceremony. File by kind instead, and keep the
            setting bible as a sibling:
          </P>
          <div className="overflow-hidden rounded border border-zinc-800">
            <div className="divide-y divide-zinc-900 bg-zinc-950/60">
              {CAMPAIGN_ROWS.map(([name, desc], i) => (
                <React.Fragment key={name}>
                  <div
                    className={`flex gap-6 px-5 py-3 text-sm ${i === 0 ? "bg-zinc-900/50" : ""}`}
                  >
                    <span className="w-40 flex-shrink-0 text-white">{name}</span>
                    <span className="text-zinc-400">{desc}</span>
                  </div>
                  {name === "Arcs" && (
                    <div className="flex gap-6 border-l-2 border-amber-400/40 px-5 py-2 pl-12 text-sm text-zinc-500">
                      Act I — Embers · Act II — Ashfall · Act III — The Pyre
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
          <p className="mt-2 text-xs text-zinc-600">
            Every row above except the World Entry is a Folder.
          </p>
          <P>
            <br />
            Deepest chains here: <code className="gcode">Arcs → Act I → Quest</code> is three
            levels, <code className="gcode">Gazetteer → Region → City → District</code> is four. You{" "}
            <em>can</em> go deeper — nothing stops you — but don&rsquo;t plan on it. Every extra
            level is one more click between you and the content, mid-session, with players waiting.
          </P>
          <H3>Rule two — nest only for &ldquo;lives inside&rdquo;; link for everything else</H3>
          <P>
            The hierarchy answers exactly one question: <em>where is this filed?</em> Every other
            relationship — this NPC belongs to that faction, this quest happens at that inn, this
            relic is in that vault — is a Weave Link inside a Folio. Links survive reorganisation.
            Nesting does not.
          </P>
          <H3>The Compendium / Chronicle bargain</H3>
          <P>Your Compendium is canon. A Chronicle holds disposable copies. Work like this:</P>
          <ol className="max-w-3xl space-y-2">
            {BARGAIN.map((step, i) => (
              <li key={step} className="flex gap-4 text-sm leading-relaxed text-zinc-400">
                <span className="w-5 flex-shrink-0 text-right font-mono text-xs text-amber-400/80">
                  {i + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
          <H3>The reveal loop — the payoff for all this structure</H3>
          <div className="mb-4 rounded border border-zinc-800 bg-zinc-900/40 p-6 text-sm leading-relaxed text-zinc-300">
            Everything starts hidden → the party meets the NPC → you share the Entry, and its public
            Folios come with it → the dramatic beat lands → mid-session you open the panel and grant
            the &ldquo;Secrets&rdquo; Folio to <em>exactly</em> the characters who learned it.
          </div>
          <P>
            Access is granted per <em>character</em>, not per player. The spy can know what the
            noble does not.
          </P>
        </Section>

        <Section num={10} title="Prepping a session in twenty minutes">
          <P>
            If your library is built the way this guide describes, prep stops being writing and
            becomes a short checklist. Set a timer and go in this order.
          </P>
          <div className="max-w-3xl space-y-4">
            {PREP.map(([time, head, rest]) => (
              <div key={time} className="flex gap-5 text-sm leading-relaxed">
                <span className="w-16 flex-shrink-0 pt-0.5 font-mono text-xs text-amber-400/80">
                  {time}
                </span>
                <span className="text-zinc-400">
                  <strong className="text-white">{head}</strong> {rest}
                </span>
              </div>
            ))}
          </div>
          <P>
            <br />
            What you are <em>not</em> doing: adding your whole library to the game, or writing
            anything new. If prep requires new writing, that is authoring, and it belongs to a
            different evening.
          </P>
        </Section>

        <Section num={11} title="Between sessions">
          <P>
            Ten minutes while it is still fresh is worth an hour next week. Four things, always the
            same four.
          </P>
          <ol className="max-w-3xl space-y-3">
            {BETWEEN.map(([head, rest], i) => (
              <li key={head} className="flex gap-4 text-sm leading-relaxed text-zinc-400">
                <span className="w-5 flex-shrink-0 text-right font-mono text-xs text-amber-400/80">
                  {i + 1}
                </span>
                <span>
                  <strong className="text-white">{head}</strong> {rest}
                </span>
              </li>
            ))}
          </ol>
          <Aside>
            Resist the fifth thing. Do not reorganise your tree after a session; you are tired and
            everything will look badly filed.
          </Aside>
        </Section>

        <Section num={12} title="Bringing in someone else's adventure">
          <P>
            An adventure someone else built here arrives as a single bundle and lands in your
            library complete — every Entry, every Folio, and all the links between them still
            pointing at the right things. It is now <em>yours</em>: a copy, not a subscription.
            Change anything.
          </P>
          <P>Four things worth doing straight after an import:</P>
          <ul className="glist max-w-3xl">
            {IMPORT_TIPS.map(([head, rest]) => (
              <li key={head}>
                <strong className="text-white">{head}</strong> {rest}
              </li>
            ))}
          </ul>
          <P>
            <br />
            Going the other way — handing <em>your</em> adventure to someone else — is the export
            described in §8. Bundle the root Folder, fix anything that points outside it, and what
            you send is a complete, playable thing.
          </P>
        </Section>

        <Section num={13} title="Moving an adventure you already have on paper">
          <P>
            A published module, or your own campaign in a folder of documents. The mistake everyone
            makes is starting at page one and typing. Do it in passes instead — each pass over the
            whole adventure, none of them requiring you to think about the next.
          </P>
          <div className="max-w-3xl space-y-4">
            {PASSES.map(([head, rest]) => (
              <div
                key={head}
                className="rounded border border-zinc-800 bg-zinc-900/40 p-5 text-sm leading-relaxed text-zinc-400"
              >
                <strong className="text-white">{head}</strong> {rest}
              </div>
            ))}
          </div>
          <P>
            <br />
            Two rules that keep this from becoming a transcription job.{" "}
            <strong className="text-white">Convert one chapter, then play it.</strong> You will
            discover your own conventions in session one and you would rather not re-do sixty
            Entries. And{" "}
            <strong className="text-white">skip the stat blocks you can look up.</strong> Move a
            creature in only when it appears in a scene you are actually going to run.
          </P>
          <P>
            One caution: anything longer than about a page will not fit in a single Folio. That is
            not an obstacle, it is the same signal as always — a page of unbroken text was two
            things pretending to be one.
          </P>
        </Section>

        <Section num={14} title="Limits worth knowing">
          <ul className="glist max-w-3xl">
            {LIMITS.map(([head, rest]) => (
              <li key={head}>
                <strong className="text-white">{head}</strong> {rest}
              </li>
            ))}
          </ul>
        </Section>

        <Section num={15} title="Seven ways to make it worse">
          <div className="max-w-3xl space-y-4">
            {WORSE.map(([head, rest]) => (
              <p key={head} className="text-sm leading-relaxed text-zinc-400">
                <strong className="text-white">{head}</strong> {rest}
              </p>
            ))}
          </div>
        </Section>
      </main>

      {/* CTA */}
      <section className="mt-8 border-t border-zinc-900 px-6 py-32 text-center">
        <div className="mx-auto max-w-2xl">
          <Kicker>Begin</Kicker>
          <h2 className="mb-6 text-4xl font-thin leading-tight tracking-wide text-white md:text-5xl">
            Your first quest is
            <br />
            ten steps away.
          </h2>
          {isAuthenticated ? (
            <>
              <p className="mb-12 text-sm text-zinc-400">Welcome back. Your world awaits.</p>
              <Link
                to="/protected/home"
                className="inline-block bg-amber-400 px-10 py-4 text-xs font-semibold uppercase tracking-widest text-zinc-950 transition-colors duration-200 hover:bg-amber-300"
              >
                Continue weaving
              </Link>
            </>
          ) : (
            <>
              <p className="mb-12 text-sm text-zinc-400">Free to start. No credit card required.</p>
              <Link
                to="/register"
                className="inline-block bg-amber-400 px-10 py-4 text-xs font-semibold uppercase tracking-widest text-zinc-950 transition-colors duration-200 hover:bg-amber-300"
              >
                Start weaving
              </Link>
            </>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="flex flex-col items-center justify-between gap-4 border-t border-zinc-900 px-8 py-10 sm:flex-row">
        <div className="flex select-none items-center gap-2">
          <img src="/logo192.png" alt="RoleWeave" className="h-5 w-5 object-contain opacity-70" />
          <span className="text-xs tracking-widest text-zinc-500">
            Role<span className="text-amber-400/80">Weave</span>
          </span>
        </div>
        <p className="text-xs text-zinc-500">© 2026 RoleWeave. All rights reserved.</p>
        <div className="flex gap-6">
          <Link
            to="/lexicon"
            className="text-xs text-zinc-500 transition-colors duration-200 hover:text-zinc-300"
          >
            Lexicon
          </Link>
          <Link
            to="/guide"
            className="text-xs text-zinc-500 transition-colors duration-200 hover:text-zinc-300"
          >
            Guide
          </Link>
          {isAuthenticated ? (
            <Link
              to="/protected/home"
              className="text-xs text-zinc-500 transition-colors duration-200 hover:text-zinc-300"
            >
              Hearth
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                className="text-xs text-zinc-500 transition-colors duration-200 hover:text-zinc-300"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                className="text-xs text-zinc-500 transition-colors duration-200 hover:text-zinc-300"
              >
                Register
              </Link>
            </>
          )}
        </div>
      </footer>

      <style>{`
        .gtoc {
          scrollbar-width: none;
        }
        .gtoc::-webkit-scrollbar {
          display: none;
        }
        .gpill {
          flex-shrink: 0;
          padding: 4px 12px;
          border: 1px solid rgb(39 39 42);
          color: rgb(113 113 122);
          font-size: 0.65rem;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          white-space: nowrap;
          transition: all 0.15s;
        }
        .gpill:hover {
          color: white;
          border-color: rgb(82 82 91);
        }
        .gcode {
          font-family: ui-monospace, monospace;
          font-size: 0.8em;
          background: rgb(39 39 42 / 0.6);
          border: 1px solid rgb(63 63 70 / 0.5);
          border-radius: 3px;
          padding: 1px 5px;
          color: rgb(251 191 36 / 0.9);
          white-space: nowrap;
        }
        .gtable {
          width: 100%;
          margin: 1rem 0 1.5rem;
          border-collapse: collapse;
          text-align: left;
        }
        .gtable th {
          font-family: ui-monospace, monospace;
          font-size: 0.6rem;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          color: rgb(113 113 122);
          font-weight: 400;
          padding: 0 1rem 0.5rem 0;
          border-bottom: 1px solid rgb(63 63 70);
        }
        .gtable td {
          font-size: 0.875rem;
          line-height: 1.6;
          color: rgb(161 161 170);
          padding: 0.75rem 1rem 0.75rem 0;
          border-bottom: 1px solid rgb(24 24 27);
          vertical-align: top;
        }
        .glist {
          list-style: disc;
          padding-left: 1.25rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          font-size: 0.875rem;
          line-height: 1.6;
          color: rgb(161 161 170);
        }
        .glist li::marker {
          color: rgb(251 191 36 / 0.6);
        }
      `}</style>
    </div>
  );
};

export default AuthoringGuide;
